import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { PageTitle, Table, Td, fmtDate } from "../../components/admin/AdminUi";
import { api, errMsg, downloadBlob } from "../../lib/api";

const CATS = [["", "All"], ["user.", "Users"], ["audit.", "Exports"]];

export default function AdminAuditLog() {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [cat, setCat] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchPage = useCallback(async (before) => {
    setBusy(true);
    try {
      const r = await api.adminAuditLog({ action: cat || undefined, before_ts: before || undefined, limit: 50 });
      setItems((prev) => (before ? [...prev, ...r.items.filter((x) => !prev.some((p) => p.id === x.id))] : r.items));
      setCursor(r.next_before_ts);
      setHasMore(r.has_more);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }, [cat]);

  useEffect(() => { fetchPage(null); }, [fetchPage]);

  const exportCsv = async () => {
    try { toast.success(`Downloaded ${downloadBlob(await api.adminAuditLogExportCsv({ action: cat || undefined }), "audit-log.csv")}`); } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div data-testid="admin-audit-log">
      <PageTitle title="Audit log" desc="Every sensitive admin action, newest first." right={
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 border border-slate-200 rounded-lg p-0.5">
            {CATS.map(([v, l]) => <button key={l} onClick={() => setCat(v)} data-testid={`audit-filter-${l.toLowerCase()}`} className={`h-7 px-3 rounded-md text-xs ${cat === v ? "bg-slate-200 text-slate-900" : "text-slate-500"}`}>{l}</button>)}
          </div>
          <button onClick={exportCsv} data-testid="audit-export-csv" className="h-8 px-3 rounded-lg border border-slate-300 text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1.5"><Download size={13} /> Export CSV</button>
        </div>
      } />
      <Table head={["When", "Action", "Admin", "Target", "Details"]} testid="audit-table" empty={!busy && items.length === 0 && "No events yet."}>
        {items.map((r) => (
          <tr key={r.id} className="hover:bg-slate-50" data-testid="audit-row">
            <Td className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.ts)}</Td>
            <Td><span className="px-1.5 py-0.5 rounded border border-slate-300 bg-slate-100 text-[11px] font-mono text-emerald-700">{r.action}</span></Td>
            <Td className="text-xs">{r.admin_name}<div className="text-slate-400">{r.admin_email}</div></Td>
            <Td className="text-xs">{r.target_label || "—"}</Td>
            <Td className="text-[11px] font-mono text-slate-500 max-w-xs truncate">{Object.keys(r.extra || {}).length ? JSON.stringify(r.extra) : ""}</Td>
          </tr>
        ))}
      </Table>
      <div className="mt-4 text-center">
        {hasMore ? (
          <button onClick={() => fetchPage(cursor)} disabled={busy} data-testid="audit-load-more" className="h-9 px-4 rounded-lg border border-slate-300 text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-2">
            {busy && <Loader2 size={13} className="animate-spin" />} Load older events
          </button>
        ) : items.length > 0 && <span className="text-xs text-slate-400">End of log.</span>}
      </div>
    </div>
  );
}
